import { useMemo } from 'react';
import { TRANSFER_JOB_MAX_HISTORY } from '@frameio/graphql-client/client-schema/transfers/state/transferJobs';
import type { AssetItemConnection } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { TRANSFER_SHEET_THUMBNAILS_COUNT } from '@frameio/shared/transfers/constants';
import { useGetTransferBatchesForAccountQuery } from '../gql/queries/generated/GetTransferBatchesForAccount';
import type { TransferBatch } from '../types/uploader';
import { useGetTransferJobs } from './useGetTransferJobs';

export const useGetTransferBatches = () => {
  const { batches: localJobs, updatedAt } = useGetTransferJobs();
  const accountId = useActiveAccountId();
  const { data, loading, error, refetch } =
    useGetTransferBatchesForAccountQuery({
      variables: {
        accountId,
        pagination: {
          pageSize: TRANSFER_JOB_MAX_HISTORY,
        },
        assetPagination: {
          first: TRANSFER_SHEET_THUMBNAILS_COUNT,
        },
      },
      skip: !accountId,
    });

  const mergedTransferBatches = useMemo<TransferBatch[] | null>(() => {
    const transferBatchesForAccount = data?.account?.transferBatches;
    if (!transferBatchesForAccount) {
      return null;
    }

    if (localJobs.length === 0) {
      return transferBatchesForAccount.result as TransferBatch[];
    }

    const localJobsMap = new Map(localJobs.map((job) => [job.id, job]));
    const result = [];
    let lastLocalJobIndex = -1;

    for (const [index, batch] of transferBatchesForAccount.result.entries()) {
      const localJob = localJobsMap.get(batch.id);
      if (localJob) {
        const res: TransferBatch = {
          ...localJob,
          paginatedAssetItems:
            (batch.paginatedAssetItems as AssetItemConnection) || null,
        };
        result.push(res);
        localJobsMap.delete(batch.id);
        lastLocalJobIndex = index;
      } else {
        result.push(batch as TransferBatch);
      }
      if (localJobsMap.size === 0) {
        break;
      }
    }

    if (result.length !== transferBatchesForAccount.result.length) {
      return result.concat(
        (transferBatchesForAccount.result as TransferBatch[]).slice(
          lastLocalJobIndex + 1
        )
      );
    }
    return result;
  }, [data, localJobs]);

  return {
    data: mergedTransferBatches,
    loading,
    error,
    localJobs,
    updatedAt,
    refetch,
  };
};
